import { formatCompact } from '@/utils/format';

export interface MarketAudience {
  market: string;
  followers: number;
}

interface MarketAudienceSplitProps {
  markets: MarketAudience[];
  /** Tighter bar + condensed legend, for narrow columns such as the compare view. */
  compact?: boolean;
  className?: string;
}

/**
 * Country splits have no brand colour, so segments borrow the palette anchors in
 * a fixed order. Indexing by the creator's ranked position keeps a market's
 * colour stable as shares change.
 */
const MARKET_COLORS = [
  'oklch(var(--primary-500))',
  'oklch(var(--accent-500))',
  'oklch(var(--secondary-500))',
  'oklch(var(--primary-700))',
  'oklch(var(--accent-700))',
];

function getMarketColor(index: number): string {
  return MARKET_COLORS[index % MARKET_COLORS.length];
}

/** Two-letter code used as the legend tile, e.g. "Kenya" -> "KE". */
function getMarketCode(market: string): string {
  return market.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase();
}

export default function MarketAudienceSplit({
  markets,
  compact = false,
  className = '',
}: MarketAudienceSplitProps) {
  const withFollowers = markets.filter((entry) => entry.followers > 0);
  const total = withFollowers.reduce((sum, entry) => sum + entry.followers, 0);

  if (total <= 0) {
    return null;
  }

  const sorted = [...withFollowers].sort((a, b) => b.followers - a.followers);

  return (
    <div className={className}>
      <div
        className={`flex w-full gap-[2px] overflow-hidden rounded-full bg-background-200 ${
          compact ? 'h-1.5' : 'h-2.5'
        }`}
        role="img"
        aria-label={`Audience split across ${sorted.length} markets`}
      >
        {sorted.map((entry, index) => {
          const share = (entry.followers / total) * 100;
          return (
            <span
              key={entry.market}
              className="h-full rounded-[2px]"
              style={{
                flexGrow: entry.followers,
                flexBasis: 0,
                backgroundColor: getMarketColor(index),
              }}
              title={`${entry.market} · ${formatCompact(entry.followers)} followers (${share.toFixed(0)}%)`}
            />
          );
        })}
      </div>

      {compact ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {sorted.map((entry, index) => {
            const share = (entry.followers / total) * 100;
            return (
              <span
                key={entry.market}
                className="inline-flex items-center gap-1 text-[11px] text-foreground-600"
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: getMarketColor(index) }}
                />
                <span className="font-label font-medium text-foreground-800">
                  {share.toFixed(0)}%
                </span>
              </span>
            );
          })}
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2.5">
          {sorted.map((entry) => {
            const share = (entry.followers / total) * 100;
            return (
              <span key={entry.market} className="inline-flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-md border border-background-200 bg-background-50 font-label text-[10px] font-semibold text-foreground-600">
                  {getMarketCode(entry.market)}
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-xs text-foreground-700">{entry.market}</span>
                  <span className="text-[11px] text-foreground-400">
                    {formatCompact(entry.followers)} · {share.toFixed(0)}%
                  </span>
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}